import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { logOut } from '../../firebase/authService';
import AppearanceOptions, { themeLabel } from '../../components/common/AppearanceOptions';
import { MenuRow, ExpandRow } from '../../components/common/ProfileRows';
import { useTheme } from '../../contexts/ThemeContext';
import PaymentCardSection from '../../components/orderer/PaymentCardSection';
import InvoiceTermsCard from '../../components/orderer/InvoiceTermsCard';
import BillingProfileCard from '../../components/orderer/BillingProfileCard';
import BroadcastModal from '../../components/common/BroadcastModal';
import { useOrgMembership } from '../../hooks/useOrgMembership';
import { COLORS, FONTS } from '../../styles/tokens';

// Bestillerens profil: konto, kort og snarveier som lå i toppmenyen
export default function OrdererProfile() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userDoc, userRoles, activeRole, setActiveRole, viewingAs, setViewingAs, refreshUserDoc } = useAuth();
  const { orgIsAdmin } = useOrgMembership();
  const { theme } = useTheme();
  const [appearanceOpen, setAppearanceOpen] = useState(false);

  // #faktura: scroll til fakturaopplysningene (lenket fra banneret og oppdragssperren)
  useEffect(() => {
    if (location.hash !== '#faktura') return;
    document.getElementById('faktura')?.scrollIntoView?.({ block: 'start' });
  }, [location.hash]);

  const name = userDoc?.name ?? '';
  const company = userDoc?.companyName ?? userDoc?.orgName ?? '';
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0].toUpperCase()).join('') || (currentUser?.email ?? '?')[0].toUpperCase();

  const driverActive  = userRoles.includes('driver')  && userDoc?.driverStatus  === 'active';
  const ordererActive = userRoles.includes('orderer') && userDoc?.ordererStatus === 'active';
  const roleRows = [];
  if (!userRoles.includes('driver')) {
    roleRows.push({ icon: 'plus', label: 'Bli sjåfør', onClick: () => navigate('/profile/add-role?role=driver') });
  } else if (userDoc?.driverStatus === 'rejected') {
    roleRows.push({ icon: 'plus', label: 'Søk på nytt som sjåfør', onClick: () => navigate('/profile/add-role?role=driver') });
  }
  if (userRoles.includes('driver') && userDoc?.driverStatus === 'pending') {
    roleRows.push({ icon: 'swap', label: 'Sjåfør-rolle', value: 'Venter godkjenning' });
  }
  if (driverActive && ordererActive && activeRole !== 'driver') {
    roleRows.push({ icon: 'swap', label: 'Bytt til sjåfør', onClick: () => { setActiveRole('driver'); navigate('/driver'); } });
  }
  if (userRoles.includes('admin')) {
    [['driver', 'Sjåfør', '/driver'], ['orderer', 'Bestiller', '/orderer'], ['admin', 'Admin', '/admin']].forEach(([v, l, p]) => {
      roleRows.push({
        icon: 'swap', label: `Vis som ${l.toLowerCase()}`,
        value: (viewingAs === v || (v === 'admin' && !viewingAs)) ? 'Aktiv' : undefined,
        onClick: () => { setViewingAs(v); navigate(p); },
      });
    });
  }

  async function handleLogout() {
    await logOut();
    navigate('/login');
  }

  const teamLabel = userDoc?.orgId ? (orgIsAdmin ? 'Team' : 'Mitt team') : 'Sett opp team';
  const teamPath = userDoc?.orgId ? '/orderer/org' : '/orderer/org/setup';

  return (
    <div style={styles.root}>
      <div style={styles.page}>
        <h1 style={styles.title}>Profil</h1>

        <div style={styles.header}>
          <div style={styles.avatar} aria-hidden="true">{initials}</div>
          <div style={{ minWidth: 0 }}>
            <div style={styles.name}>{name || 'Bestiller'}</div>
            {company && <div style={styles.company}>{company}</div>}
            <div style={styles.email}>{currentUser?.email}</div>
          </div>
        </div>

        <div id="faktura"><BillingProfileCard /></div>

        <PaymentCardSection userDoc={userDoc} currentUser={currentUser} onRefresh={refreshUserDoc} />

        <InvoiceTermsCard />

        <div style={styles.list}>
          <MenuRow icon="layers" label="Prosjekter" onClick={() => navigate('/orderer/projects')} />
          <MenuRow icon="users" label={teamLabel} onClick={() => navigate(teamPath)} />
          <MenuRow icon="chat" label="Gi tilbakemelding" onClick={() => navigate('/orderer/tilbakemelding')} />
          <MenuRow icon="lock" label="Personvern" onClick={() => navigate('/personvern')} />
          <ExpandRow name="utseende" icon="sun" label="Utseende" value={themeLabel(theme)} open={appearanceOpen} onToggle={() => setAppearanceOpen(o => !o)}>
            <AppearanceOptions />
          </ExpandRow>
          {roleRows.map(r => <MenuRow key={r.label} icon={r.icon} label={r.label} value={r.value} onClick={r.onClick} />)}
          <MenuRow icon="out" label="Logg ut" onClick={handleLogout} danger />
        </div>
      </div>
      <BroadcastModal />
    </div>
  );
}

const styles = {
  root:    { minHeight: '100vh', background: COLORS.bg },
  page:    { maxWidth: 600, margin: '0 auto', padding: '24px 16px', paddingTop: 'max(32px, env(safe-area-inset-top))' },
  title:   { fontFamily: FONTS.display, fontSize: 40, lineHeight: 1, fontWeight: 700, margin: '12px 0 16px', color: COLORS.textDark },
  header:  { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 },
  avatar:  { width: 72, height: 72, borderRadius: '50%', flexShrink: 0, background: COLORS.brandPanel, color: COLORS.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONTS.display, fontSize: 30, fontWeight: 700 },
  name:    { fontFamily: FONTS.display, fontSize: 30, lineHeight: 1.05, fontWeight: 700, color: COLORS.textDark },
  company: { fontSize: 14, color: COLORS.textMid, marginTop: 3 },
  email:   { fontSize: 12, color: COLORS.textLight, marginTop: 2, overflowWrap: 'anywhere' },
  list:    { background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 20, padding: '0 16px' },
};
